// @flow

import styled from '@emotion/styled'
import * as React from 'react'

import { colors, media, variables } from '@toggl/style'

import * as Button from '../button'
import { useIsOpeningEffect } from '../hooks/useIsOpeningEffect'
import * as text from '../text'

import ExpandedMenu from './expandedMenu'
import Links from './links'

export { default as ExpandedMenu } from './expandedMenu'
export { default as MenuContent } from './menuContent'
export { Product, ProductsGroup } from './product'
export * from './backSocial'

type Props = {
  cta: React.Node,
  background?: string,
  children: React.Node,
  login: React.Node,
  logo?: React.Element<any>,
  onMenuClose?: () => void,
  expandedMenu?: React.Node,
  logoWidth: [string, string],
  onToggle: (open: boolean) => void,
  open: boolean,
}

const ANIMATION_TIMER = 190

export const Header = ({
  cta,
  background,
  children,
  login,
  logo,
  onMenuClose,
  expandedMenu,
  logoWidth,
  onToggle,
  open,
}: Props) => {
  const isOpening = useIsOpeningEffect(open, ANIMATION_TIMER)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [hasScrolled, setHasScrolled] = React.useState(false)

  const listenScrollEvent = () => {
    if (window.scrollY > 0) {
      setHasScrolled(true)
    } else {
      setHasScrolled(false)
    }
  }

  React.useEffect(() => {
    window.addEventListener('scroll', listenScrollEvent)
    // we have a race condition here, it is possible that scroll event is emitted before our event listener is hooked.
    // so calling the listenScrollEvent again to check for initial scroll
    listenScrollEvent()
  }, [])

  return (
    <Wrapper open={open} className="wrapper--nav">
      <HeaderLine
        className={'header--line' + (hasScrolled ? ' pageIsScrolled' : '')}
        background={background}
        open={open}
        isOpening={isOpening}
      >
        <LogoWrapper logoWidth={logoWidth}>{logo}</LogoWrapper>

        <DesktopLinks open={open} className="desktop--links">
          {children}
          <HeaderCTA>
            {login}
            {cta}
          </HeaderCTA>
        </DesktopLinks>

        <MobileLinksWrapper open={mobileOpen} className="mobile--links">
          <Links>{children}</Links>
          <CtaWrapper>
            <CtaButtonWrapper>{cta}</CtaButtonWrapper>
            <div>{login}</div>
          </CtaWrapper>
        </MobileLinksWrapper>
        <Burger
          open={mobileOpen}
          onClick={() => {
            onToggle(!open)
            setMobileOpen(!mobileOpen)
          }}
        >
          <div />
          <div />
          <div />
        </Burger>
      </HeaderLine>

      {onMenuClose && (
        <StyledExpandedMenu
          logoWidth={logoWidth}
          animationTimer={ANIMATION_TIMER}
          onClose={onMenuClose}
        >
          {expandedMenu || null}
        </StyledExpandedMenu>
      )}
    </Wrapper>
  )
}

const HeaderCTA = styled.div`
  ${media.mq[1]} {
    display: flex;
    align-items: center;
    transform: translateY(-50%);
    > *:not(:last-child) {
      margin-right: 30px;
    }
  }
  ${media.mq[2]} {
    transform: translateY(0);
  }
`

const Wrapper: any = styled.div`
  display: flex;
  flex-wrap: wrap;
  height: ${variables.headerHeight}px;
  position: relative;
  width: 100%;

  ${media.mq[0]} {
    pointer-events: all;
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 0;
  }

  ${media.mq[1]} {
    display: block;
    overflow: hidden;
    height: ${(p) => (p.open ? '565' : variables.headerHeightTablet)}px;
    transition: height ${ANIMATION_TIMER}ms;
    pointer-events: all;
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 0;
    &::after {
      display: block;
      content: '';
      width: 100%;
      position: absolute;
      background-color: ${(p) => p.theme.backgroundDesktop};
      height: ${({ open }) => (open ? '100%' : '0%')};
      z-index: -1;
      padding: 0;
      top: 0;
      transition: height ${ANIMATION_TIMER}ms;
    }
  }

  ${media.mq[2]} {
    height: ${(p) => (p.open ? '565' : variables.headerHeightFull)}px;
  }
`

const DesktopLinks = styled.nav`
  ${text.paragraph3};
  display: none;
  width: 100%;
  align-items: center;
  justify-content: space-between;

  > *:not(:last-child) {
    margin-right: 20px;
    ${media.mq[2]} {
      margin-right: 3%;
    }
  }

  a {
    text-decoration: none;
  }
  ${media.mq[1]} {
    display: flex;
    flex: 1 1 100%;
  }
  ${media.mq[2]} {
    flex: 1;
  }
`

const MobileLinksWrapper = styled.div`
  ${text.heading4};
  background: ${(p) => p.theme.background};
  flex-direction: column;
  margin: 0 -20px;
  padding: 30px 20px;
  position: absolute;
  top: 100%;
  height: calc(100vh - 100%);
  transition: all 300ms linear;
  width: 100%;
  overflow: hidden;

  display: ${(p) => (p.open ? 'flex' : 'none')};

  ul > li {
    margin-bottom: 20px;

    a,
    button,
    button:hover {
      text-decoration: none;
      color: ${colors.fadedPurple};
    }
  }

  ${media.mq[1]} {
    display: none;
  }
`

const CtaButtonWrapper = styled.div`
  margin-bottom: 20px;

  ${media.mq[1]} {
    margin: 0;
  }
`

const LogoWrapper = styled.div`
  width: ${(p) => p.logoWidth[0]};
  display: flex;
  flex-shrink: 0;
  margin-right: 30px;

  ${media.mq[2]} {
    width: ${(p) => p.logoWidth[1]};
  }

  a {
    display: flex;
    flex: 1;
    ${media.mq[1]} {
      height: 52px;
    }
  }

  svg {
    width: 100%;
    ${media.mq[1]} {
      margin-top: 15px;
    }
    ${media.mq[2]} {
      margin-top: 0;
    }
  }
`

const HeaderLine: React.ComponentType<any> = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  background-color: ${(p) => p.theme.background};
  border-bottom: solid 1px ${(p) => p.theme.borderColor};
  justify-content: space-between;
  padding: 0 20px;
  height: 64px;
  transition: ${(p) => (p.open ? 'none' : 'background 0.15s ease-in')};

  ${media.mq[1]} {
    flex-wrap: wrap;
    height: ${variables.headerHeightTablet}px;
    border-bottom: none;
    background-color: ${({ open, background = 'transparent' }) =>
      open ? 'transparent' : background};

    ${Button.Secondary}, ${Button.HeaderButtonWrapper}, a {
      color: ${(p) => (p.open ? colors.darkPurple : colors.almostWhite)};
      transition: color
        ${({ isOpening }) => (isOpening ? '10ms' : `${ANIMATION_TIMER}ms`)};
    }

    ${LogoWrapper} svg {
      transition: ${({ isOpening }) => (isOpening ? '10' : ANIMATION_TIMER)}ms;
      transition-property: fill;
      transition-delay: ${({ isOpening }) =>
        isOpening ? '0' : ANIMATION_TIMER / 2}ms;
    }
    &.pageIsScrolled {
      background: ${(p) => (p.open ? colors.beige : colors.darkPurple)};
      border-bottom: ${(p) =>
        p.open ? 'none' : `1px solid ${colors.fadedPurpleAlt}`};
    }
  }
  ${media.mq[2]} {
    flex-wrap: no-wrap;
    height: ${variables.headerHeightFull}px;
  }
`

const StyledExpandedMenu = styled(ExpandedMenu)`
  position: absolute;
  top: ${variables.headerHeight}px;

  ${media.mq[1]} {
    top: unset;
    position: relative;
    background: transparent;
  }
`

const Burger: React.ComponentType<any> = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 24px;
  height: 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;

  &:focus {
    outline: none;
  }

  ${media.mq[1]} {
    display: none;
  }

  div {
    width: 24px;
    height: 2px;
    background: ${colors.darkPurple};
    border-radius: 10px;
    position: relative;
    transform-origin: 3px;

    :first-of-type {
      transform: ${({ open }) => (open ? 'rotate(45deg)' : 'rotate(0)')};
    }

    :nth-of-type(2) {
      opacity: ${({ open }) => (open ? '0' : '1')};
      transform: ${({ open }) => (open ? 'translateX(20px)' : 'translateX(0)')};
    }

    :nth-of-type(3) {
      transform: ${({ open }) => (open ? 'rotate(-45deg)' : 'rotate(0)')};
    }
  }
`

const CtaWrapper: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 50px;

  ${media.mq[1]} {
    flex-direction: row;
    justify-content: space-between;
    margin-top: 0;
    margin-left: auto;
  }
`
